import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Modal,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Divider,
  Snackbar,
  Alert,
  TextField,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PrintIcon from "@mui/icons-material/Print";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { useNavigate, useLocation } from "react-router-dom";

const getCSRF = () =>
  document.cookie
    .split("; ")
    .find((row) => row.startsWith("csrftoken="))
    ?.split("=")[1] || "";

const comprobanteToBackend = (label) => {
  if (label === "Nota de Crédito") return "NOTA_CREDITO";
  return "FACTURA";
};

const backendToRouteSegment = (tipo) => {
  if (tipo === "NOTA_CREDITO") return "nota-credito";
  return "factura";
};

const getPdfName = (tipo, numero) => {
  if (tipo === "NOTA_CREDITO") {
    return `NotaCredito_${numero || ""}.pdf`;
  }
  return `Factura_${numero || ""}.pdf`;
};

const getTicketName = (tipo, numero) => {
  if (tipo === "NOTA_CREDITO") {
    return `NotaCredito_${numero || ""}.pdf`;
  }
  return `Ticket_${numero || ""}.pdf`;
};

const clearOptionalSellerFields = (info) => {
  const next = { ...info };

  const sellerKeys = [
    "vendedor",
    "vendedor_id",
    "vendedorId",
    "seller",
    "seller_id",
    "sellerId",
    "usuario_vendedor",
    "usuario_vendedor_id",
    "responsable_venta",
    "responsable_venta_id",
  ];

  sellerKeys.forEach((key) => {
    const value = next[key];
    if (
      value === undefined ||
      value === null ||
      value === "" ||
      value === "null" ||
      value === "undefined"
    ) {
      delete next[key];
    }
  });

  return next;
};

const InvoiceCheck = ({
  invoiceInfo,
  productDescripcion,
  invoiceData,
  payments,
  documentoId,
  setDocumentoId,
  invoiceStatus,
  setInvoiceStatus,
  isDraft,
  setIsDraft,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalState, setModalState] = useState("loading");
  const [modalMessage, setModalMessage] = useState("");

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [sendingSri, setSendingSri] = useState(false);

  const [emailOpen, setEmailOpen] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailDocNumero, setEmailDocNumero] = useState("");
  const [emailRazon, setEmailRazon] = useState("");
  const [emailValue, setEmailValue] = useState("");
  const [emailError, setEmailError] = useState("");

  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });

  const statusUpper = (invoiceStatus || "").toUpperCase();
  const isAuthorized = statusUpper === "AUTORIZADO";
  const canShowDocument = Boolean(documentoId);
  const actionLocked = savingDraft || sendingSri;

  const prefill = useMemo(() => location?.state?.prefill_invoice || {}, [location]);
  const prefillInfo = useMemo(() => prefill.info || {}, [prefill]);

  const tipoDocumentoBackend = useMemo(
    () =>
      comprobanteToBackend(
        invoiceInfo?.tipo_comprobante || prefillInfo?.tipo_comprobante || "Factura"
      ),
    [invoiceInfo, prefillInfo]
  );

  const numeroDocumento = useMemo(() => {
    return (
      invoiceInfo?.numero_documento ||
      prefillInfo?.numero_documento ||
      invoiceInfo?.numero ||
      prefillInfo?.numero ||
      ""
    );
  }, [invoiceInfo, prefillInfo]);

  const routeSegment = useMemo(
    () => backendToRouteSegment(tipoDocumentoBackend),
    [tipoDocumentoBackend]
  );

  const buildPayload = () => {
    const infoPayload = clearOptionalSellerFields({
      ...prefillInfo,
      ...invoiceInfo,
      tipo_documento: tipoDocumentoBackend,
      establecimiento:
        invoiceInfo?.establecimiento ||
        prefillInfo?.establecimiento ||
        "",
      punto_emision:
        invoiceInfo?.punto_emision ||
        prefillInfo?.punto_emision ||
        "",
      descripcion_factura: productDescripcion,
    });

    return {
      documento_id: documentoId || undefined,
      info: infoPayload,
      productos: invoiceData,
      pagos: Array.isArray(payments) ? payments : [],
    };
  };

  const applyServerState = (data) => {
    const docId = data.documento_id || null;
    const estado = (data.estado || "").toUpperCase();
    const borrador = Boolean(data.es_borrador);

    if (docId) setDocumentoId(docId);
    setInvoiceStatus(estado);
    setIsDraft(borrador);

    if (estado === "AUTORIZADO") {
      setModalState("success");
      setModalMessage(
        data.sri_mensaje || "El documento fue autorizado correctamente por el SRI."
      );
      return;
    }

    if (borrador || estado === "BORRADOR") {
      setModalState("success");
      setModalMessage(
        data.mensaje ||
          "El documento fue guardado correctamente. No se enviará al SRI en este momento y podrás seguir editándolo después."
      );
      return;
    }

    if (estado === "NO_AUTORIZADO") {
      setModalState("error");
      setModalMessage(
        data.sri_mensaje ||
          "El SRI no autorizó el documento. Revisa el documento guardado para continuar con la gestión."
      );
      return;
    }

    if (estado === "ERROR") {
      setModalState("info");
      setModalMessage(
        data.sri_mensaje ||
          "El documento fue generado y el sistema manejará el reenvío automáticamente si el SRI presenta novedades. No es necesario crearlo nuevamente."
      );
      return;
    }

    setModalState("info");
    setModalMessage(
      data.sri_mensaje ||
        "El documento fue generado correctamente. Si el SRI presenta demoras o novedades, el sistema maneja reenvío automático. No es necesario crearlo nuevamente."
    );
  };

  const handleSaveDraft = async () => {
    if (actionLocked) return;

    setSaveConfirmOpen(false);
    setSavingDraft(true);
    setModalState("loading");
    setModalMessage("Guardando documento...");
    setModalOpen(true);

    const payload = buildPayload();
    const endpoint = documentoId
      ? `/facturas/documento/borrador/actualizar/${documentoId}/`
      : "/facturas/documento/borrador/guardar/";

    try {
      const res = await fetch(endpoint, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCSRF(),
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.ok) {
        setModalState("error");
        setModalMessage(data.error || "No fue posible guardar el documento.");
        return;
      }

      applyServerState(data);
    } catch {
      setModalState("error");
      setModalMessage("No fue posible guardar el documento en este momento.");
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSendSri = async () => {
    if (actionLocked) return;

    setSendConfirmOpen(false);
    setSendingSri(true);
    setModalState("loading");
    setModalMessage("Firmando y enviando documento al SRI...");
    setModalOpen(true);

    const payload = buildPayload();
    const endpoint = documentoId
      ? `/facturas/documento/enviar/${documentoId}/`
      : "/facturas/documento/crear/";

    try {
      const res = await fetch(endpoint, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCSRF(),
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.ok) {
        setModalState("error");
        setModalMessage(data.error || "No fue posible procesar el documento.");
        return;
      }

      applyServerState(data);
    } catch {
      setModalState("error");
      setModalMessage("No fue posible comunicarse con el servidor.");
    } finally {
      setSendingSri(false);
    }
  };

  const descargarArchivo = async (url, defaultFilename = "documento.pdf") => {
    try {
      const resp = await fetch(url, { credentials: "include" });
      if (!resp.ok) {
        setSnack({ open: true, msg: "No se pudo descargar el archivo", sev: "error" });
        return;
      }

      const blob = await resp.blob();
      const disposition = resp.headers.get("Content-Disposition") || "";
      let filename = defaultFilename;
      const match = disposition.match(/filename="?([^"]+)"?/);

      if (match && match[1]) filename = match[1];

      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
    } catch {
      setSnack({ open: true, msg: "No se pudo descargar el archivo", sev: "error" });
    }
  };

  const openEmailModal = async () => {
    if (!documentoId || !isAuthorized) return;

    setEmailOpen(true);
    setEmailLoading(true);
    setEmailError("");
    setEmailDocNumero("");
    setEmailRazon("");
    setEmailValue("");

    try {
      await fetch("/facturas/csrf/", { credentials: "include" });

      const r = await fetch("/facturas/enviar/info/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCSRF(),
        },
        body: JSON.stringify({ documento_id: documentoId }),
      });

      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.ok) {
        throw new Error(data.error || "No se pudo cargar la información de envío");
      }

      setEmailDocNumero(data.documento || "");
      setEmailRazon(data.razon_social || "");
      setEmailValue(data.correo || "");
    } catch (e) {
      setEmailError(e?.message || "Error al cargar información");
    } finally {
      setEmailLoading(false);
    }
  };

  const validateEmail = (v) => {
    const s = (v || "").trim();
    if (!s) return "Correo requerido";
    if (!s.includes("@")) return "Correo inválido";
    return "";
  };

  const sendEmail = async () => {
    if (!isAuthorized) {
      setSnack({
        open: true,
        msg: "Solo puedes enviar por correo documentos autorizados",
        sev: "warning",
      });
      return;
    }

    const err = validateEmail(emailValue);
    setEmailError(err);
    if (err) return;
    if (!documentoId) return;

    setEmailSending(true);
    try {
      await fetch("/facturas/csrf/", { credentials: "include" });

      const r = await fetch("/facturas/enviar/enviar/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCSRF(),
        },
        body: JSON.stringify({ documento_id: documentoId, correo: emailValue.trim() }),
      });

      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.ok) {
        throw new Error(data.error || "No se pudo enviar el correo");
      }

      setSnack({ open: true, msg: "Correo enviado correctamente", sev: "success" });
      setEmailOpen(false);
    } catch (e) {
      setSnack({ open: true, msg: e?.message || "Error al enviar correo", sev: "error" });
    } finally {
      setEmailSending(false);
    }
  };

  const getBanner = () => {
    if (!documentoId) return null;

    if (isAuthorized) {
      return {
        severity: "success",
        text: "El documento fue autorizado correctamente por el SRI. Ya puedes revisarlo, imprimirlo o enviarlo al destinatario.",
      };
    }

    if (isDraft) {
      return {
        severity: "info",
        text: "El documento está guardado y sigue en edición. No se ha enviado al SRI todavía. Puedes volver a modificarlo cuando lo necesites.",
      };
    }

    return {
      severity: "info",
      text: "El sistema ha generado el documento. No es necesario crearlo nuevamente. Si el SRI presenta novedades o demoras, el sistema maneja reenvío automático. Puedes revisar o descargar el comprobante.",
    };
  };

  const banner = getBanner();

  return (
    <>
      {banner && (
        <Alert severity={banner.severity} sx={{ mb: 2, mt: 3 }}>
          {banner.text}
        </Alert>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3, flexWrap: "wrap" }}>
        {canShowDocument ? (
          isDraft ? (
            <>
              <Button
                startIcon={<SaveOutlinedIcon />}
                variant="outlined"
                sx={{ borderColor: "#1565C0", color: "#1565C0" }}
                onClick={() => setSaveConfirmOpen(true)}
                disabled={actionLocked}
              >
                {savingDraft ? "Guardando..." : "Guardar"}
              </Button>

              <Button
                startIcon={<SendRoundedIcon />}
                variant="contained"
                sx={{ bgcolor: "#1565C0" }}
                onClick={() => setSendConfirmOpen(true)}
                disabled={actionLocked}
              >
                {sendingSri ? "Enviando..." : "Guardar y enviar"}
              </Button>
            </>
          ) : (
            <>
              {isAuthorized && (
                <Button
                  startIcon={<MailOutlineIcon />}
                  variant="contained"
                  sx={{ bgcolor: "#0870A2" }}
                  onClick={openEmailModal}
                >
                  Enviar
                </Button>
              )}

              <Button
                startIcon={<PrintIcon />}
                variant="contained"
                sx={{ bgcolor: "#0870A2" }}
                onClick={() =>
                  descargarArchivo(
                    `/facturas/pdf/${documentoId}/?tipo_documento=${tipoDocumentoBackend}`,
                    getPdfName(tipoDocumentoBackend, numeroDocumento)
                  )
                }
              >
                Imprimir
              </Button>

              <Button
                startIcon={<ConfirmationNumberIcon />}
                variant="contained"
                sx={{ bgcolor: "#0870A2" }}
                onClick={() =>
                  descargarArchivo(
                    `/facturas/ticket/${documentoId}/?tipo_documento=${tipoDocumentoBackend}`,
                    getTicketName(tipoDocumentoBackend, numeroDocumento)
                  )
                }
              >
                Ticket
              </Button>

              <Button
                variant="contained"
                sx={{ bgcolor: "#1565C0" }}
                onClick={() =>
                  navigate(`/admin-panel/${routeSegment}/V-${documentoId}`, {
                    state: {
                      tipo_documento: tipoDocumentoBackend,
                    },
                  })
                }
              >
                Ver Documento
              </Button>
            </>
          )
        ) : (
          <>
            <Button
              startIcon={<SaveOutlinedIcon />}
              variant="outlined"
              sx={{ borderColor: "#1565C0", color: "#1565C0" }}
              onClick={() => setSaveConfirmOpen(true)}
              disabled={actionLocked}
            >
              {savingDraft ? "Guardando..." : "Guardar"}
            </Button>

            <Button
              startIcon={<SendRoundedIcon />}
              variant="contained"
              sx={{ bgcolor: "#1565C0" }}
              onClick={() => setSendConfirmOpen(true)}
              disabled={actionLocked}
            >
              {sendingSri ? "Enviando..." : "Guardar y enviar"}
            </Button>
          </>
        )}
      </Box>

      <Dialog
        open={saveConfirmOpen}
        onClose={savingDraft ? undefined : () => setSaveConfirmOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Guardar documento</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box
              sx={{
                minWidth: 48,
                width: 48,
                height: 48,
                borderRadius: "50%",
                bgcolor: "#E3F2FD",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <DescriptionOutlinedIcon sx={{ color: "#1565C0" }} />
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>
                Este documento se guardará sin enviarse al SRI
              </Typography>
              <Typography sx={{ color: "#555", lineHeight: 1.7 }}>
                Podrás cerrar esta pantalla, volver después y seguir modificándolo antes de enviarlo.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSaveConfirmOpen(false)} disabled={savingDraft}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveDraft}
            disabled={savingDraft}
            sx={{ bgcolor: "#1565C0" }}
          >
            {savingDraft ? "Guardando..." : "Aceptar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={sendConfirmOpen}
        onClose={sendingSri ? undefined : () => setSendConfirmOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Guardar y enviar documento</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box
              sx={{
                minWidth: 48,
                width: 48,
                height: 48,
                borderRadius: "50%",
                bgcolor: "#E8F5E9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SendRoundedIcon sx={{ color: "#2E7D32" }} />
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>
                Este documento se firmará y se enviará al SRI
              </Typography>
              <Typography sx={{ color: "#555", lineHeight: 1.7 }}>
                Después de continuar, el sistema generará el documento y gestionará el envío al SRI.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSendConfirmOpen(false)} disabled={sendingSri}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSendSri}
            disabled={sendingSri}
            sx={{ bgcolor: "#1565C0" }}
          >
            {sendingSri ? "Procesando..." : "Aceptar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Modal open={modalOpen} onClose={modalState === "loading" ? undefined : () => setModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "#fff",
            p: 4,
            borderRadius: 2,
            width: 520,
            maxWidth: "92vw",
            textAlign: "center",
            whiteSpace: "pre-line",
          }}
        >
          {modalState === "loading" && (
            <>
              <CircularProgress sx={{ mb: 3 }} />
              <Typography>{modalMessage}</Typography>
            </>
          )}

          {modalState === "success" && (
            <>
              <CheckCircleOutlineIcon sx={{ fontSize: 60, color: "green", mb: 2 }} />
              <Typography sx={{ mb: 3 }}>{modalMessage}</Typography>
              <Button variant="contained" sx={{ bgcolor: "#1565C0" }} onClick={() => setModalOpen(false)}>
                Aceptar
              </Button>
            </>
          )}

          {modalState === "info" && (
            <>
              <InfoOutlinedIcon sx={{ fontSize: 60, color: "#f59e0b", mb: 2 }} />
              <Typography sx={{ mb: 3 }}>{modalMessage}</Typography>
              <Button variant="contained" sx={{ bgcolor: "#1565C0" }} onClick={() => setModalOpen(false)}>
                Entendido
              </Button>
            </>
          )}

          {modalState === "error" && (
            <>
              <ErrorOutlineIcon sx={{ fontSize: 60, color: "#d32f2f", mb: 2 }} />
              <Typography sx={{ mb: 3 }}>{modalMessage}</Typography>
              <Button variant="contained" sx={{ bgcolor: "#1565C0" }} onClick={() => setModalOpen(false)}>
                Entendido
              </Button>
            </>
          )}
        </Box>
      </Modal>

      <Dialog open={emailOpen} onClose={emailSending ? undefined : () => setEmailOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800 }}>Enviar documento por correo</DialogTitle>
        <DialogContent dividers>
          {emailLoading ? (
            <Box sx={{ py: 3, textAlign: "center" }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Cargando información…</Typography>
            </Box>
          ) : (
            <>
              {emailError ? (
                <Alert severity="error">{emailError}</Alert>
              ) : (
                <>
                  <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                    Se enviará el documento {emailDocNumero || "-"}
                  </Typography>
                  <Typography sx={{ color: "#555", mb: 2 }}>{emailRazon || "-"}</Typography>

                  <Divider sx={{ mb: 2 }} />

                  <TextField
                    label="Correo de destino"
                    value={emailValue}
                    onChange={(e) => {
                      setEmailValue(e.target.value);
                      if (emailError) setEmailError("");
                    }}
                    fullWidth
                    error={Boolean(emailError)}
                    helperText={emailError || "Verifica el correo antes de enviar"}
                  />
                </>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEmailOpen(false)} disabled={emailSending} sx={{ fontWeight: 700 }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={sendEmail}
            disabled={emailLoading || emailSending || !emailValue.trim() || Boolean(emailError) || !isAuthorized}
            sx={{ bgcolor: "#1565C0", fontWeight: 800 }}
          >
            {emailSending ? "Enviando…" : "Enviar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3200}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snack.sev}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default InvoiceCheck;